import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faTimes, faChevronRight, faChevronDown,
  faCheck, faShieldAlt, faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import owaspWstgService from '../../services/owaspWstgService';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

const severityColors = {
  critical: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-800 dark:text-red-300' },
  high: { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-800 dark:text-orange-300' },
  medium: { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-800 dark:text-yellow-300' },
  low: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-800 dark:text-blue-300' },
  info: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-300' }
};

const categoryColorMap = {
  INFO: 'bg-blue-500', CONF: 'bg-gray-500', IDNT: 'bg-purple-500',
  ATHN: 'bg-green-500', ATHZ: 'bg-yellow-500', SESS: 'bg-orange-500',
  INPV: 'bg-red-500', ERRH: 'bg-pink-500', CRYP: 'bg-indigo-500',
  BUSL: 'bg-teal-500', CLNT: 'bg-cyan-500', APIT: 'bg-lime-500'
};

/**
 * WstgTestSelector - Browse and select OWASP WSTG tests
 *
 * @param {Array} selectedTests - Currently selected test objects
 * @param {Function} onSelectionChange - Callback with updated test array
 */
const WstgTestSelector = ({ selectedTests = [], onSelectionChange }) => {
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [previewTest, setPreviewTest] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await owaspWstgService.getAll();
        setTests(data);
        // Auto-expand first category
        if (data.length > 0) {
          const firstCat = data[0].categoryCode;
          setExpandedCategories({ [firstCat]: true });
        }
      } catch (err) {
        console.error('Failed to load WSTG tests:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const selectedIds = useMemo(() => new Set(selectedTests.map(t => t.testId)), [selectedTests]);

  const grouped = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = term
      ? tests.filter(t =>
          t.testId.toLowerCase().includes(term) ||
          t.name.toLowerCase().includes(term) ||
          (t.description && t.description.toLowerCase().includes(term)) ||
          t.categoryCode.toLowerCase().includes(term)
        )
      : tests;

    const groups = {};
    filtered.forEach(test => {
      if (!groups[test.categoryCode]) groups[test.categoryCode] = [];
      groups[test.categoryCode].push(test);
    });
    return groups;
  }, [tests, searchTerm]);

  // Auto-expand on search
  useEffect(() => {
    if (searchTerm.trim()) {
      const allCats = {};
      Object.keys(grouped).forEach(c => { allCats[c] = true; });
      setExpandedCategories(allCats);
    }
  }, [searchTerm, grouped]);

  const toggleTest = (test) => {
    if (selectedIds.has(test.testId)) {
      onSelectionChange(selectedTests.filter(t => t.testId !== test.testId));
    } else {
      onSelectionChange([...selectedTests, test]);
    }
  };

  const toggleCategory = (code) => {
    setExpandedCategories(prev => ({ ...prev, [code]: !prev[code] }));
  };

  const selectAllInCategory = (code) => {
    const catTests = grouped[code] || [];
    const allSelected = catTests.every(t => selectedIds.has(t.testId));
    if (allSelected) {
      const catIds = new Set(catTests.map(t => t.testId));
      onSelectionChange(selectedTests.filter(t => !catIds.has(t.testId)));
    } else {
      const newTests = catTests.filter(t => !selectedIds.has(t.testId));
      onSelectionChange([...selectedTests, ...newTests]);
    }
  };

  const bg = darkMode ? 'bg-gray-800' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const hover = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100';

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${textMuted}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3"></div>
      </div>
    );
  }

  return (
    <div className={`flex h-full ${text}`}>
      {/* LEFT: Category tree with checkboxes */}
      <div className={`${previewTest ? 'w-1/2' : 'w-full'} flex flex-col border-r ${border}`}>
        {/* Search */}
        <div className={`p-3 border-b ${border}`}>
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textMuted}`} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={t('campaigns.searchWstgTests') || 'Search WSTG tests by ID, name, or description...'}
              className={`w-full pl-10 pr-8 py-2 rounded-lg border text-sm ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-500'
              }`}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className={`absolute right-3 top-1/2 -translate-y-1/2 ${textMuted}`}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        </div>

        {/* Tree */}
        <div className="flex-1 overflow-y-auto">
          {Object.keys(grouped).length === 0 ? (
            <div className={`p-4 text-center ${textMuted}`}>{t('common.noResults') || 'No results found'}</div>
          ) : (
            Object.entries(grouped).map(([code, catTests]) => {
              const config = owaspWstgService.getCategoryConfig(`WSTG-${code}`);
              const selectedInCat = catTests.filter(t => selectedIds.has(t.testId)).length;
              const allSelected = selectedInCat === catTests.length;

              return (
                <div key={code} className={`border-b ${border}`}>
                  {/* Category header */}
                  <div className={`flex items-center gap-2 p-3 ${hover} cursor-pointer`}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => selectAllInCategory(code)}
                      className="rounded"
                      onClick={e => e.stopPropagation()}
                    />
                    <button
                      onClick={() => toggleCategory(code)}
                      className="flex-1 flex items-center gap-2 text-left"
                    >
                      <FontAwesomeIcon
                        icon={expandedCategories[code] ? faChevronDown : faChevronRight}
                        className={`w-3 ${textMuted}`}
                      />
                      <span className={`w-2 h-2 rounded-full ${categoryColorMap[code] || 'bg-gray-400'}`}></span>
                      <span className={`font-medium text-sm ${text}`}>{config.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                        {selectedInCat > 0 ? `${selectedInCat}/` : ''}{catTests.length}
                      </span>
                    </button>
                  </div>

                  {/* Tests */}
                  {expandedCategories[code] && (
                    <div className="pl-4 pb-1">
                      {catTests.map(test => {
                        const isChecked = selectedIds.has(test.testId);
                        const sev = severityColors[test.severity] || severityColors.info;
                        return (
                          <div
                            key={test.testId}
                            className={`flex items-center gap-2 py-1.5 px-2 rounded group ${
                              previewTest?.testId === test.testId
                                ? darkMode ? 'bg-orange-900/20 border border-orange-700' : 'bg-orange-50 border border-orange-300'
                                : hover
                            } cursor-pointer`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleTest(test)}
                              className="rounded flex-shrink-0"
                            />
                            <div
                              className="flex-1 flex items-center gap-2"
                              onClick={() => setPreviewTest(test)}
                            >
                              <span className={`font-mono text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'
                              }`}>
                                {test.testId}
                              </span>
                              <span className={`text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                {test.name}
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${sev.bg} ${sev.text}`}>
                                {test.severity}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT: Preview panel */}
      {previewTest && (
        <div className="w-1/2 flex flex-col overflow-y-auto">
          <div className={`p-4 border-b ${border}`}>
            <div className="flex items-start justify-between">
              <div>
                <span className={`font-mono text-sm px-2 py-1 rounded mr-2 ${
                  darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'
                }`}>
                  {previewTest.testId}
                </span>
                <h3 className={`text-lg font-bold mt-2 ${text}`}>{previewTest.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    severityColors[previewTest.severity]?.bg || ''
                  } ${severityColors[previewTest.severity]?.text || ''}`}>
                    {previewTest.severity}
                  </span>
                  <span className={`w-2 h-2 rounded-full ${categoryColorMap[previewTest.categoryCode] || 'bg-gray-400'}`}></span>
                  <span className={`text-xs ${textMuted}`}>
                    {owaspWstgService.getCategoryConfig(`WSTG-${previewTest.categoryCode}`).name}
                  </span>
                </div>
              </div>
              <button onClick={() => setPreviewTest(null)} className={`p-1 rounded ${hover} ${textMuted}`}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          <div className="flex-1 p-4">
            <h4 className={`text-sm font-medium mb-2 ${textMuted}`}>Description</h4>
            <p className={`text-sm leading-relaxed mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {previewTest.description || 'No description available.'}
            </p>

            {/* MITRE cross-links */}
            {previewTest.mitreMapping?.length > 0 && (
              <div>
                <h4 className={`text-sm font-medium mb-2 ${textMuted}`}>
                  <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
                  MITRE ATT&CK Mappings
                </h4>
                <div className="flex flex-wrap gap-1">
                  {previewTest.mitreMapping.map((m, i) => (
                    <span key={i} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                      darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'
                    }`}>
                      <span className="font-mono font-medium">{m.techniqueId}</span>
                      <span className="opacity-75">{m.techniqueName}</span>
                      <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>({m.tactic})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* OWASP Top 10 */}
            {previewTest.owaspTop10?.length > 0 && (
              <div className="mt-4">
                <h4 className={`text-sm font-medium mb-2 ${textMuted}`}>OWASP Top 10</h4>
                <div className="flex flex-wrap gap-1">
                  {previewTest.owaspTop10.map((item, i) => (
                    <span key={i} className={`text-xs px-2 py-1 rounded ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                    }`}>{item}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Select button */}
          <div className={`p-4 border-t ${border}`}>
            {selectedIds.has(previewTest.testId) ? (
              <button
                onClick={() => toggleTest(previewTest)}
                className={`w-full py-2 rounded-lg text-sm ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}
              >
                <FontAwesomeIcon icon={faCheck} className="mr-2" />
                Selected - Click to Remove
              </button>
            ) : (
              <button
                onClick={() => toggleTest(previewTest)}
                className="w-full py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
              >
                Select {previewTest.testId}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WstgTestSelector;
